import { type Response, sendGet, sendPost, sendPostFormData } from '../hooks/use-fetch'
import { type BuildingEditModel } from '../models/BuildingEditModel'
import { type EEMOrganization } from '../models/EEMOrganization'
import { type OrganizationDetailsModel } from '../models/OrganizationDetailsModel'
import { type OrganizationListModel } from '../models/OrganizationListModel'
import { type OrganizationSummaryModel } from '../models/OrganizationSummaryModel'
import { type OrganizationOption } from '../models/requestObjects/CommonObjects'
import { type SelectableOrganization } from '../models/SelectableOrganization'
import { type User } from '../models/User'
import { type OrgSearchValues } from '../pages/pageComponents/organizations/OrganizationSearch'
import { type ActiveStatus, type OrganizationStatus, type SelectableOption } from '../utils/genericTypes'

export async function GetOrganization (orgId: string): Promise<Response<OrganizationDetailsModel>> {
  return await sendGet(`Organization/GetDetails?orgId=${orgId}`)
}

export async function GetBuildingDetails (buildingId: string): Promise<Response<BuildingEditModel>> {
  return await sendGet(`Organization/GetBuildingDetails?buildingId=${buildingId}`)
}

export async function GetOrgSummary (orgId: string): Promise<Response<OrganizationSummaryModel>> {
  return await sendGet(`Organization/GetSummary?orgId=${orgId}`)
}

export async function GetAdminContacts (orgId: string): Promise<Response<User[]>> {
  return await sendGet(`Organization/GetAdminContacts?orgId=${orgId}`)
}

export async function GetOrgList (searchValues: OrgSearchValues): Promise<Response<OrganizationListModel[]>> {
  return await sendPost('Organization/GetList', searchValues)
}

export async function GetAvailableISDsFromEEM (): Promise<Response<SelectableOption[]>> {
  return await sendGet('Organization/GetAvailableISDsFromEEM')
}

export async function GetAvailableBuildingsFromEEM (orgId: string): Promise<Response<EEMOrganization[]>> {
  return await sendGet(`Organization/GetAvailableBuildingsFromEEM?orgId=${orgId}`)
}

export async function GetAvailableDistrictsFromEEM (isdId: string): Promise<Response<EEMOrganization[]>> {
  return await sendGet(`Organization/GetAvailableDistrictsFromEEM?isdId=${isdId}`)
}

export async function AddBuilding (orgId: string, eemOrgId: string): Promise<void> {
  await sendPost('Organization/AddBuilding', { orgId, eemOrgId })
}

export async function AddDistrict (parentOrgId: string, eemOrgId: string): Promise<void> {
  await sendPost('Organization/AddDistrict', { parentOrgId, eemOrgId })
}

export async function AddRCN (parentOrgId: string, rcnName: string, isdEEMIds: number[]): Promise<Response> {
  return await sendPost('Organization/AddRCN', { parentOrgId, rcnName, isdEEMIds })
}

export async function ToggleOnboardingTask (orgId: string, taskKey: string): Promise<void> {
  await sendPost('Organization/ToggleOnboardingTask', { orgId, taskKey })
}

export async function UpdateStatus (orgId: string, status: OrganizationStatus): Promise<void> {
  await sendPost('Organization/UpdateStatus', { orgId, status })
}

export async function UpdateBuildingStatus (buildingId: string, status: ActiveStatus): Promise<void> {
  await sendPost('Organization/UpdateBuildingStatus', { buildingId, status })
}

export async function UploadBuildingLogo (buildingId: string, file: File): Promise<Response<string>> {
  const fileData = new FormData()
  fileData.append('file', file)
  fileData.append('buildingId', buildingId)
  return await sendPostFormData('Organization/UploadBuildingLogo', fileData)
}

export async function GetOrganizationsForUser (): Promise<Response<OrganizationListModel[]>> {
  return await sendGet('Organization/GetOrganizationsForUser')
}

export async function GetAvailableOrganizations (): Promise<Response<SelectableOrganization[]>> {
  return await sendGet('Organization/GetAvailableOrganizations')
}

export async function GetAvailableBuildings (): Promise<Response<SelectableOption[]>> {
  return await sendGet('Organization/GetAvailableBuildings')
}

export async function GetBuildingsForOrganization (orgId: number): Promise<Response<SelectableOption[]>> {
  return await sendGet(`Organization/GetBuildingsForOrganization?orgId=${orgId}`)
}

export async function GetRcnFromOrganization (orgId: number): Promise<Response<OrganizationOption>> {
  return await sendGet(`Organization/GetRcnFromOrganization?orgId=${orgId}`)
}
